import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { get } from "../api/axios"
import endpoints from "../api/endpoints"
import {
  getCityAdminCitySelectOptions,
  isCityAdminUser,
} from "../helpers/cityPortalRoles"

const extractPublicCityList = result => {
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return []
}

const formatPublicCityLabel = city => {
  if (!city) return ""
  if (typeof city === "string") return city.trim()
  const name = city.city || city.name || ""
  const state = city.state || ""
  const country = city.country || ""
  if (name && state) return `${name}, ${state}`
  if (name && country) return `${name}, ${country}`
  return name || String(city.id || "")
}

/**
 * City options for admin listing filters.
 * - City admin (`type: admin`): only assigned cities from /me (no public cities API).
 * - Super admin: full public cities list.
 */
const useAdminCityFilterOptions = ({ enabled = true } = {}) => {
  const currentUser = useSelector(state => state.auth.currentUser)
  const isCityAdmin = isCityAdminUser(currentUser)
  const cityAdminOptions = useMemo(
    () => getCityAdminCitySelectOptions(currentUser),
    [currentUser],
  )

  const [cityOptions, setCityOptions] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setCityOptions([])
      setIsLoading(false)
      return undefined
    }

    if (isCityAdmin) {
      setCityOptions(cityAdminOptions)
      setIsLoading(false)
      return undefined
    }

    let cancelled = false

    const loadCities = async () => {
      setIsLoading(true)
      try {
        const response = await get(endpoints.getCites, {
          params: { page: 1, page_size: 200 },
          skipErrorToast: true,
        })
        if (cancelled || !response?.data?.success) {
          if (!cancelled) setCityOptions([])
          return
        }
        setCityOptions(
          extractPublicCityList(response.data.result)
            .map(city => ({
              value: String(city.id || ""),
              label: formatPublicCityLabel(city),
            }))
            .filter(option => option.value && option.label),
        )
      } catch {
        if (!cancelled) setCityOptions([])
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadCities()
    return () => {
      cancelled = true
    }
  }, [cityAdminOptions, enabled, isCityAdmin])

  return {
    cityOptions,
    isLoading,
    isCityAdmin,
  }
}

export default useAdminCityFilterOptions
